import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import updateLocale from 'dayjs/plugin/updateLocale';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import 'dayjs/locale/en';
import 'dayjs/locale/ar';
import 'dayjs/locale/he';

/* ---------- plugins ---------- */
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);

/* ---------------------------------------------------
 *  Month-Year picker that ALWAYS emits JS Date objects
 *  at 00:00 UTC (‖ 01-Jan or 01-MM)
 * -------------------------------------------------- */
function CustomMonthYearIndividualDatePicker({
    year,            // Date | ISO | null  → 01-Jan UTC recommended
    month,           // Date | ISO | null  → 01-MM UTC recommended
    onYearChange,    // receives JS Date @ 00:00 UTC, 01-Jan-YYYY
    onMonthChange,   // receives JS Date @ 00:00 UTC, 01-MM-YYYY
    labelYear,
    labelMonth,
    height = '48px',
    widthInner = '100%',
    widthOuter = '100%',
    flip = false,
}) {
    /* ---------- locale ---------- */
    const { i18n } = useTranslation();

    const direction = i18n.dir();

    /* ---------- state ---------- */
    const [openYear, setOpenYear] = useState(false);
    const [openMonth, setOpenMonth] = useState(false);

    const [inputYearValue, setInputYearValue] = useState(
        year ? dayjs.utc(year).format('YYYY') : '',
    );
    const [inputMonthValue, setInputMonthValue] = useState(
        month ? dayjs.utc(month).format('MM') : '',
    );

    /* ---------- helpers ---------- */
    /** 01-Jan 00:00 UTC of given year */
    const buildUtcYearStart = (y) => new Date(Date.UTC(y, 0, 1, 0, 0, 0, 0));

    /** 01-MM 00:00 UTC (month 0-11) in given year */
    const buildUtcMonthStart = (y, m) =>
        new Date(Date.UTC(y, m, 1, 0, 0, 0, 0));

    /* ---------- debounced callbacks ---------- */
    const debouncedYear = useCallback(debounce(onYearChange, 300), [onYearChange]);
    const debouncedMonth = useCallback(debounce(onMonthChange, 300), [onMonthChange]);

    /* ---------- year handler ---------- */
    const handleYearPickerChange = (newVal) => {
        const typing = !openYear;
        setInputYearValue(newVal ? newVal.format('YYYY') : '');

        if (!newVal?.isValid()) return;
        const yStart = buildUtcYearStart(newVal.year());

        if (typing) debouncedYear(yStart);
        else {
            setOpenYear(false);
            onYearChange(yStart);
        }
    };

    /* ---------- month handler ---------- */
    const handleMonthPickerChange = (newVal) => {
        const typing = !openMonth;
        setInputMonthValue(newVal ? newVal.format('MM') : '');

        if (!newVal?.isValid()) return;

        // Get the current year from the year prop using UTC
        const baseYear = year ? dayjs.utc(year).year() : dayjs.utc().year();

        // Build UTC month start using the base year and the new month
        const mStart = buildUtcMonthStart(baseYear, newVal.month());

        if (typing) debouncedMonth(mStart);
        else {
            setOpenMonth(false);
            onMonthChange(mStart);
        }
    };
    useEffect(() => {
        dayjs.locale(i18n.language);
    }, [i18n.language]);
    /* ---------- pickers ---------- */
    const yearPicker = (
        <DatePicker
            open={openYear}
            onOpen={() => setOpenYear(true)}
            onClose={() => setOpenYear(false)}
            label={labelYear}
            views={['year']}
            value={year ? dayjs.utc(year) : null}
            inputValue={inputYearValue}
            onChange={handleYearPickerChange}
            format="YYYY"
            placeholder="YYYY"
            slotProps={{
                textField: {
                    sx: { width: widthInner },
                    InputProps: {
                        disableUnderline: true,
                        className: `custom-box-sizing-border-box ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'
                            }`,
                        sx: {
                            height,
                            padding: '0 10px',
                            borderRadius: 'var(--Spacing-md, 8px)',
                            background: 'var(--bg-input, #F5F5F6)',
                            border: '1px solid transparent',
                        },
                    },
                    inputProps: {
                        style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                    },
                },
            }}
        />
    );

    const monthPicker = (
        <DatePicker
            open={openMonth}
            onOpen={() => setOpenMonth(true)}
            onClose={() => setOpenMonth(false)}
            label={labelMonth}
            views={['month']}
            value={month ? dayjs.utc(month) : null}
            inputValue={inputMonthValue}
            onChange={handleMonthPickerChange}
            format="MM"
            placeholder="MM"
            slotProps={{
                textField: {
                    sx: { width: widthInner },
                    InputProps: {
                        disableUnderline: true,
                        className: `custom-box-sizing-border-box ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'
                            }`,
                        sx: {
                            height,
                            padding: '0 10px',
                            borderRadius: 'var(--Spacing-md, 8px)',
                            background: 'var(--bg-input, #F5F5F6)',
                            border: '1px solid transparent',
                        },
                    },
                    inputProps: {
                        style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                    },
                },
            }}
        />
    );

    /* ---------- render ---------- */
    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={i18n.language}
        >
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '16px',
                    width: widthOuter,
                }}
            >
                {flip ? (
                    <>
                        <div style={{ flex: '1 1 0' }}>{monthPicker}</div>
                        <div style={{ flex: '1 1 0' }}>{yearPicker}</div>
                    </>
                ) : (
                    <>
                        <div style={{ flex: '1 1 0' }}>{yearPicker}</div>
                        <div style={{ flex: '1 1 0' }}>{monthPicker}</div>
                    </>
                )}
            </div>
        </LocalizationProvider>
    );
}

export default CustomMonthYearIndividualDatePicker;
